import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  activeProviderAccountUuid,
  clearProviderSessionRestoreAttempt,
  deactivateProviderSession,
  hasActiveProviderTokens,
  invalidateProviderSessionRestoreEpoch,
  reloadActiveProviderUser,
} from "@/lib/providerSession";
import { clearDemoSession, getDemoUser, isDemoSession } from "@/lib/demoMode";

export type UserRole = "guest" | "client" | "staff" | "manager" | "supervisor" | "director" | "owner";
export type UserStatus = "active" | "pending" | "blocked" | "banned" | "rejected";
export type ApprovalStatus = "pending" | "approved" | "rejected";
export type AuthSource = "legacy" | "provider";

export interface User {
  id: string;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  phone?: string;
  avatar_url?: string;
  role: UserRole;
  // Status fields - use 'status' as primary source of truth
  status?: UserStatus;
  approval_status?: ApprovalStatus;
  is_blocked?: boolean;
  block_reason?: string | null;
  // Timestamps
  registration_completed_at?: string;
  created_at?: string;
  auth_source?: AuthSource;
  account_uuid?: string;
  account_type?: "studio_owner" | "studio_staff" | "wenlix_internal_manager";
  provider_subject?: string;
  organization_id?: string;
  studio_id?: string | null;
  business_role?: string;
  permissions?: string[];
  business_api?: "core" | "staff" | "support";
  support_only?: boolean;
}

interface AuthState {
  user: User | null;
  authSource: AuthSource | null;
  isAuthenticated: boolean;
  isDemo: boolean;
  isHydrated: boolean;
  setUser: (user: User) => void;
  setProviderUser: (user: User) => void;
  setDemoUser: (user: User) => void;
  clearUser: () => void;
  logout: () => void;
  setHydrated: (state: boolean) => void;
}

const initialDemoUser = getDemoUser();
let authRevision = 0;
let unpublishedHydrationRevision: number | null = null;

const authStorage = createJSONStorage(() => ({
  getItem: (name: string) => localStorage.getItem(name),
  setItem: (name: string, value: string) => {
    // A local demo session must not overwrite a real persisted auth session.
    if (isDemoSession()) return;
    // Hiding an unverified projection is local to this tab, not peer logout.
    // Only its synchronous revision is skipped; an explicit auth action gets
    // a newer revision and continues to publish its normal invalidation.
    if (unpublishedHydrationRevision === authRevision) return;
    localStorage.setItem(name, value);
  },
  removeItem: (name: string) => localStorage.removeItem(name),
}));

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: initialDemoUser,
      authSource: initialDemoUser ? "legacy" : null,
      isAuthenticated: initialDemoUser !== null,
      isDemo: initialDemoUser !== null,
      isHydrated: false,

      setUser: (user) => {
        authRevision++;
        set((state) => ({
          user,
          authSource: "legacy",
          isAuthenticated: true,
          isDemo: state.isDemo && isDemoSession(),
        }));
      },

      setProviderUser: (user) => {
        authRevision++;
        set({
          user: { ...user, auth_source: "provider" },
          authSource: "provider",
          isAuthenticated: true,
          isDemo: false,
        });
      },

      setDemoUser: (user) => {
        authRevision++;
        set({
          user,
          authSource: "legacy",
          isAuthenticated: true,
          isDemo: true,
        });
      },

      clearUser: () => {
        authRevision++;
        const wasDemo = isDemoSession();
        if (!wasDemo) clearProviderSessionRestoreAttempt();
        set({
          user: null,
          authSource: null,
          isAuthenticated: false,
          isDemo: false,
        });
        if (wasDemo) clearDemoSession();
      },

      logout: () => {
        authRevision++;
        const wasDemo = isDemoSession();
        // Clear tokens
        if (!wasDemo) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
        }
        if (!wasDemo) {
          invalidateProviderSessionRestoreEpoch();
          deactivateProviderSession();
        }
        set({
          user: null,
          authSource: null,
          isAuthenticated: false,
          isDemo: false,
        });
        if (wasDemo) clearDemoSession();
      },

      setHydrated: (state) => set({ isHydrated: state }),
    }),
    {
      name: "wenlix-auth",
      storage: authStorage,
      partialize: (state) => ({
        user: state.user,
        authSource: state.authSource,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => async (state) => {
        const restoredDemoUser = getDemoUser();
        if (state && restoredDemoUser) {
          state.setDemoUser(restoredDemoUser);
          state.setHydrated(true);
          return;
        }

        // Profile metadata is shared across tabs; provider credentials and the
        // selected principal belong to this tab. Never display a peer's user
        // with this tab's tokens while a fresh canonical projection is pending.
        const providerAccount = hasActiveProviderTokens()
          ? activeProviderAccountUuid()
          : null;
        if (state && providerAccount && (
          state.authSource !== "provider" || !state.isAuthenticated ||
          state.user?.account_uuid !== providerAccount ||
          state.user.id !== providerAccount ||
          state.user.provider_subject !== providerAccount
        )) {
          unpublishedHydrationRevision = authRevision + 1;
          try {
            state.clearUser();
          } finally {
            unpublishedHydrationRevision = null;
          }
          const revision = authRevision;
          try {
            const user = await reloadActiveProviderUser();
            if (revision !== authRevision || activeProviderAccountUuid() !== providerAccount) return;
            if (user.auth_source !== "provider" || user.account_uuid !== providerAccount ||
              user.id !== providerAccount || user.provider_subject !== providerAccount) {
              throw new Error("provider hydration principal mismatch");
            }
            state.setProviderUser(user);
          } catch {
            if (revision === authRevision) state.clearUser();
          } finally {
            state.setHydrated(true);
          }
          return;
        }

        // After rehydration, check if tokens still exist
        const hasTokens = state?.authSource === "provider"
          ? hasActiveProviderTokens()
          : !!(localStorage.getItem("access_token") && localStorage.getItem("refresh_token"));

        if (state) {
          // Provider sessions are restored through a fresh PKCE exchange in the route gate.
          // Legacy sessions still fail closed immediately when their persisted tokens are gone.
          if (!hasTokens && state.isAuthenticated && state.authSource !== "provider") {
            state.clearUser();
          }
          state.setHydrated(true);
        }
      },
    }
  )
);

// Role check helpers
export const isAdmin = (role?: UserRole) =>
  role === "supervisor" || role === "director" || role === "owner";

export const isDirectorOrAbove = (role?: UserRole) =>
  role === "director" || role === "owner";

export const isOwner = (role?: UserRole) =>
  role === "owner";
